import { Box, VStack, Heading, Text, Code, Divider, Container } from "@chakra-ui/react";
import HeaderMenus from './HeaderMenus';

function APIDocsOrchestrate() {
  return (
    <Box textAlign="left" fontSize="md">
      <HeaderMenus />
      <Container maxW="container.xl" py={10}>
        <VStack spacing={8} align="stretch">
          <Box>
            <Heading as="h1" size="xl" mb={4}>V2 Case Processing API</Heading>
            <Text>
              This document provides details on the V2 Case Processing API endpoints. The primary goal of this API is to provide a single, robust endpoint for partners to process a case from start to finish, eliminating the need for multiple sequential calls.
            </Text>
            <Text mt={2}>
              Individual endpoints for each step are also available for flexibility, debugging, or custom workflows.
            </Text>
          </Box>

          <Divider />

          {/* Consolidated Workflow Endpoint */}
          <Box id="consolidated-workflow">
            <Heading as="h2" size="lg" mb={4}>Consolidated Workflow Endpoint</Heading>
            <Text>
              This is the primary endpoint for partners. It orchestrates the entire case processing workflow in a single call.
            </Text>

            <Heading as="h3" size="md" mt={6} mb={2}>POST /v2/cases/process-complete</Heading>
            <Text><strong>Description:</strong> Creates a case, uploads files, performs CPT coding, assigns carrier information, applies carrier rules, and runs a medical necessity analysis in one atomic operation.</Text>
            <Text><strong>Authorization:</strong> Requires authentication (JWT).</Text>
            <Text><strong>Request Type:</strong> <Code>multipart/form-data</Code></Text>
            
            <Heading as="h4" size="sm" mt={4} mb={2}>Request Parts:</Heading>
            <Text>1. <Code>case_data</Code> (form field): A JSON string containing metadata for the entire workflow.</Text>
            <Code p={4} display="block" whiteSpace="pre" overflowX="auto">
{`{
  "external_case_id": "string",
  "case_type": "string",
  "carrier": {
    "payer_type": "string",
    "state_full": "string"
  },
  "analysis": {
    "mode": "string",
    "claim_type": "string"
  }
}`}
            </Code>
            <Text mt={2}>2. <Code>files</Code> (file field): One or more files to be uploaded and associated with the case.</Text>

            <Heading as="h4" size="sm" mt={4} mb={2}>Success Response (200 OK):</Heading>
            <Code p={4} display="block" whiteSpace="pre" overflowX="auto">
{`{
  "message": "Case processed successfully",
  "results": {
    "case_creation": { "..." },
    "file_upload": { "..." },
    "cpt_coding": { "..." },
    "carrier_assignment": { "..." },
    "carrier_rules": { "..." },
    "analysis": { "..." }
  },
  "timing": {
    "total_duration_ms": 12345,
    "steps": { "..." }
  }
}`}
            </Code>
            <Text mt={2}>- <Code>results</Code>: An object containing the detailed output from each step of the workflow.</Text>
            <Text mt={1}>- <Code>timing</Code>: An object detailing the performance of each step and the total duration.</Text>

            <Heading as="h4" size="sm" mt={4} mb={2}>Error Response:</Heading>
            <Text>Returns an appropriate HTTP status code (e.g., 400, 500) with a JSON body describing the error.</Text>
            <Code p={4} display="block" whiteSpace="pre" overflowX="auto">
{`{
  "message": "Error during step X: [details]",
  "failed_step": "step_name",
  "partial_results": { "..." }
}`}
            </Code>
          </Box>

          <Divider />

          {/* Individual Step Endpoints */}
          <Box id="individual-steps">
            <Heading as="h2" size="lg" mb={4}>Individual Step Endpoints</Heading>
            <Text>These endpoints provide granular control over each step of the workflow. They are useful for debugging, custom integrations, or partial processing.</Text>

            {/* 1. Create Case */}
            <Box mt={6}>
              <Heading as="h3" size="md" mb={2}>1. Create Case</Heading>
              <Text><strong>Endpoint:</strong> <Code>POST /v2/cases</Code></Text>
              <Text><strong>Description:</strong> Creates a new external case and associated encounter record.</Text>
              <Heading as="h4" size="sm" mt={4} mb={2}>Request Body:</Heading>
              <Code p={4} display="block" whiteSpace="pre" overflowX="auto">
{`{
  "external_case_id": "string",
  "case_type": "string",
  "client_id": "string"
}`}
              </Code>
              <Text mt={1}>- <Code>case_type</Code> (string, optional): The type of case (e.g., "IONM Modern").</Text>
              <Text mt={1}>- <Code>client_id</Code> (string, optional): The ID of the client associated with this case.</Text>
              <Heading as="h4" size="sm" mt={4} mb={2}>Success Response (201 Created):</Heading>
              <Code p={4} display="block" whiteSpace="pre" overflowX="auto">
{`{
  "message": "External case created successfully",
  "case_id": "internal_case_uuid"
}`}
              </Code>
            </Box>

            {/* 2. Upload Files */}
            <Box mt={6}>
              <Heading as="h3" size="md" mb={2}>2. Upload Files</Heading>
              <Text><strong>Endpoint:</strong> <Code>POST /v2/files/upload/&lt;case_id&gt;</Code></Text>
              <Text><strong>Description:</strong> Uploads a single file and associates it with an existing case.</Text>
              <Text><strong>URL Parameters:</strong> <Code>case_id</Code> (string, required): The internal UUID of the case.</Text>
              <Text><strong>Request Type:</strong> <Code>multipart/form-data</Code> with one <Code>file</Code> part and optional form fields (<Code>file_type_id</Code>, <Code>document_category</Code>, <Code>claim_id</Code>).</Text>
              <Heading as="h4" size="sm" mt={4} mb={2}>Success Response (201 Created or 200 OK):</Heading>
              <Code p={4} display="block" whiteSpace="pre" overflowX="auto">
{`{
  "message": "File uploaded successfully",
  "file_id": "string"
}`}
              </Code>
            </Box>

            {/* 3. Process CPT Coding */}
            <Box mt={6}>
              <Heading as="h3" size="md" mb={2}>3. Process CPT Coding</Heading>
              <Text><strong>Endpoint:</strong> <Code>POST /v2/cpt/coding</Code></Text>
              <Text><strong>Description:</strong> Initiates the CPT coding process for a case. Can be run in the background (default) or synchronously.</Text>
              <Heading as="h4" size="sm" mt={4} mb={2}>Request Body:</Heading>
              <Code p={4} display="block" whiteSpace="pre" overflowX="auto">
{`{
  "case_id": "string",
  "case_type": "string",
  "background": true
}`}
              </Code>
              <Heading as="h4" size="sm" mt={4} mb={2}>Success Response (202 Accepted for background):</Heading>
              <Code p={4} display="block" whiteSpace="pre" overflowX="auto">
{`{
  "status": "processing",
  "message": "CPT coding started in background",
  "case_id": "string",
  "encounter_id": "string"
}`}
              </Code>
            </Box>

            {/* 4. Assign Carrier */}
            <Box mt={6}>
              <Heading as="h3" size="md" mb={2}>4. Assign Carrier</Heading>
              <Text><strong>Endpoint:</strong> <Code>POST /v2/cases/&lt;case_id&gt;/carrier</Code></Text>
              <Text><strong>Description:</strong> Assigns carrier and payer information to a case.</Text>
              <Text><strong>URL Parameters:</strong> <Code>case_id</Code> (string, required): The external case ID.</Text>
              <Heading as="h4" size="sm" mt={4} mb={2}>Request Body:</Heading>
              <Code p={4} display="block" whiteSpace="pre" overflowX="auto">
{`{
  "payer_type": "string",
  "state_full": "string",
  "carrier_name": "string"
}`}
              </Code>
              <Heading as="h4" size="sm" mt={4} mb={2}>Success Response (200 OK):</Heading>
              <Code p={4} display="block" whiteSpace="pre" overflowX="auto">
{`{
  "success": true,
  "message": "Carrier assigned successfully",
  "carrier_info": {
    "id": 123,
    "carrier_name": "string",
    "payer_type": "string",
    "state_full": "string",
    "state_abbr": "string",
    "status": "Active"
  }
}`}
              </Code>
            </Box>

            {/* 5. Apply Carrier Rules */}
            <Box mt={6}>
              <Heading as="h3" size="md" mb={2}>5. Apply Carrier Rules</Heading>
              <Text><strong>Endpoint:</strong> <Code>POST /v2/cases/&lt;case_id&gt;/carrier-rules</Code></Text>
              <Text><strong>Description:</strong> Applies payer-specific coding rules to a case after a carrier has been assigned.</Text>
              <Text><strong>URL Parameters:</strong> <Code>case_id</Code> (string, required): The external case ID.</Text>
              <Heading as="h4" size="sm" mt={4} mb={2}>Request Body:</Heading>
              <Code p={4} display="block" whiteSpace="pre" overflowX="auto">
{`{
  "force_refresh": false
}`}
              </Code>
              <Text mt={1}>- <Code>force_refresh</Code> (boolean, optional): If true, re-applies rules even if results already exist.</Text>
              <Heading as="h4" size="sm" mt={4} mb={2}>Success Response (200 OK):</Heading>
              <Code p={4} display="block" whiteSpace="pre" overflowX="auto">
{`{
  "success": true,
  "results": {
    "pro": {
      "success": true,
      "adjusted_codes": { "...": "..." },
      "changes_applied": [ "..." ]
    }
  },
  "summary": {
    "total_claims": 1,
    "successful": 1,
    "failed": 0
  },
  "carrier_info": { "..." }
}`}
              </Code>
            </Box>

            {/* 6. Run Medical Necessity Analysis */}
            <Box mt={6}>
              <Heading as="h3" size="md" mb={2}>6. Run Medical Necessity Analysis</Heading>
              <Text><strong>Endpoint:</strong> <Code>POST /v2/analysis/case</Code></Text>
              <Text><strong>Description:</strong> Runs the final medical necessity analysis on a fully processed case.</Text>
              <Heading as="h4" size="sm" mt={4} mb={2}>Request Body:</Heading>
              <Code p={4} display="block" whiteSpace="pre" overflowX="auto">
{`{
  "external_case_id": "string",
  "mode": "string",
  "claim_type": "string",
  "force_rerun": false
}`}
              </Code>
              <Heading as="h4" size="sm" mt={4} mb={2}>Success Response (200 OK):</Heading>
              <Text>Returns a comprehensive JSON object with the full analysis results.</Text>
            </Box>

          </Box>
        </VStack>
      </Container>
    </Box>
  );
}

export default APIDocsOrchestrate;